/**
 * Menú de autenticación en el header (RFC-0020 · RFC-0024 · RFC-0025).
 *
 *  - Anónimo: botón "Iniciar sesión" → modal de login. No hay registro
 *    público ni prueba gratis desde el 2026-10-08 (decisión de Antonio
 *    del 30-sep): las cuentas las abre TerraKairos desde la consola.
 *  - Con sesión: indicador de plan (prueba, vencimiento, cupo), menú con
 *    "Mi plan", cambiar contraseña, gestionar usuarios (admin) y la
 *    consola de clientes y planes (super admin).
 *  - must_change_password fuerza el modal de cambio de contraseña.
 *  - Enlaces que llegan por correo (RFC-0026): `?token=` abre "crea /
 *    restablece tu contraseña" y `?panel=cuenta|consola` el panel
 *    correspondiente al entrar. `?acceso=solicitar` (correos viejos) sólo
 *    se limpia de la URL: el registro está cerrado.
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { RenewalRequest } from './RenewalRequest';
import { UpgradePlans } from './UpgradePlans';
import { openResultsList } from '../lib/signup';
import { useAuth } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import type { AccessRequest, AdminUser, MyOrganization, OrganizationSeats } from '../lib/types';
import {
  ApiKeysSection,
  daysUntil,
  DeliveryNotice,
  type DeliveryResult,
  errMsg,
  fmtDate,
  fmtNum,
  Modal,
  msg,
  PlanSummary,
  ROLE_LABELS,
  UserRowActions,
} from './adminShared';

// Sólo la usa el dueño de la plataforma: se descarga al abrirla, no en la
// carga inicial de cada visitante.
const SuperAdminConsole = lazy(() => import('./SuperAdminConsole').then((m) => ({ default: m.SuperAdminConsole })));

interface LinkParams {
  token: string | null;
  panel: 'cuenta' | 'consola' | null;
}

let linkParamsCache: LinkParams | null = null;

/**
 * Lee UNA vez los parámetros de los enlaces de correo y los quita de la
 * URL: el token no debe quedarse en el historial, en marcadores ni en una
 * captura de pantalla. Memorizado porque StrictMode llama dos veces a los
 * inicializadores de estado.
 */
function takeLinkParams(): LinkParams {
  if (linkParamsCache) return linkParamsCache;
  const url = new URL(window.location.href);
  const p = url.searchParams;
  const raw = p.get('token');
  const panel = p.get('panel');
  linkParamsCache = {
    token: raw && /^[a-f0-9]{64}$/.test(raw) ? raw : null,
    panel: panel === 'cuenta' || panel === 'consola' ? panel : null,
  };
  if (p.has('token') || p.has('acceso') || p.has('panel')) {
    p.delete('token'); p.delete('acceso'); p.delete('panel');
    const qs = p.toString();
    window.history.replaceState(window.history.state, '', `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`);
  }
  return linkParamsCache;
}

export function AuthMenu() {
  const { user, loading, logout, legal } = useAuth();
  const [links] = useState(takeLinkParams);
  const [open, setOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSuper, setShowSuper] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(links.token);
  const [pendingPanel, setPendingPanel] = useState<LinkParams['panel']>(links.panel);
  const [forgot, setForgot] = useState<{ email: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // must_change_password: forzar el modal hasta que cambie.
  useEffect(() => {
    if (user?.mustChangePassword) setShowChangePw(true);
  }, [user?.mustChangePassword]);

  // Cualquier parte de la app puede pedir el modal de acceso (la lista
  // cerrada, la ficha por deep-link) sin tener que pasarse callbacks por
  // media docena de componentes.
  useEffect(() => {
    const onOpen = () => setShowLogin(true);
    const onPlan = () => setShowPlan(true);
    window.addEventListener('tk:open-login', onOpen);
    window.addEventListener('tk:open-plan', onPlan);
    return () => {
      window.removeEventListener('tk:open-login', onOpen);
      window.removeEventListener('tk:open-plan', onPlan);
    };
  }, []);

  // `?panel=` de un correo: se abre al tener sesión (pidiendo login si hace falta).
  useEffect(() => {
    if (loading || !pendingPanel || linkToken) return;
    if (!user) { setShowLogin(true); return; }
    if (user.mustChangePassword) return;
    if (pendingPanel === 'consola' && user.role === 'superadmin') setShowSuper(true);
    else if (user.role === 'admin' || user.role === 'superadmin') setShowAdmin(true);
    else if (user.organization) setShowPlan(true);
    setPendingPanel(null);
  }, [loading, user, pendingPanel, linkToken]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (loading) return null;

  // Van SIEMPRE en la misma posición del árbol: al completar un enlace se
  // abre sesión y el menú cambia de rama; si el modal cambiara de lugar,
  // React lo remontaría y volvería a inspeccionar un token ya usado.
  const linkModals = (
    <>
      {linkToken && (
        <TokenModal
          token={linkToken}
          onClose={() => setLinkToken(null)}
          onForgot={(email) => { setLinkToken(null); setForgot({ email }); }}
        />
      )}
      {forgot && <ForgotPasswordModal initialEmail={forgot.email} onClose={() => setForgot(null)} />}
    </>
  );

  if (!user) {
    return (
      <>
        <div className="auth-anon">
          <button className="auth-login-btn" onClick={() => setShowLogin(true)}>
            Iniciar sesión
          </button>
          {showLogin && (
            <LoginModal
              onClose={() => setShowLogin(false)}
              onForgot={(email) => { setShowLogin(false); setForgot({ email }); }}
            />
          )}
        </div>
        {linkModals}
      </>
    );
  }

  const initial = (user.name ?? user.email).trim().charAt(0).toUpperCase();

  return (
    <>
    <div className="auth-menu" ref={menuRef}>
      <PlanPill onOpen={() => setShowPlan(true)} />
      <button className="auth-avatar-btn" onClick={() => setOpen((v) => !v)} title={user.email}>
        <span className="auth-avatar">{initial}</span>
        <span className="auth-avatar-label">{user.name ?? user.email}</span>
      </button>
      {open && (
        <div className="auth-dropdown">
          <div className="auth-dropdown-head">
            <div className="auth-dropdown-email">{user.email}</div>
            <span className={`auth-role auth-role-${user.role}`}>
              {ROLE_LABELS[user.role] ?? user.role}
            </span>
            {user.organization && (
              <div className="auth-dropdown-org" title={`Plan ${user.entitlements?.planName ?? user.organization.plan}${user.organization.activeUntil ? ` · vence ${fmtDate(user.organization.activeUntil)}` : ''}`}>
                {user.organization.name} · {user.entitlements?.planName ?? user.organization.plan}
              </div>
            )}
          </div>
          {user.organization && (
            <button className="auth-dropdown-item" onClick={() => { setShowPlan(true); setOpen(false); }}>
              Mi plan y consumo
            </button>
          )}
          <button className="auth-dropdown-item" onClick={() => { setShowChangePw(true); setOpen(false); }}>
            Cambiar contraseña
          </button>
          {(user.role === 'admin' || user.role === 'superadmin') && (
            <button className="auth-dropdown-item" onClick={() => { setShowAdmin(true); setOpen(false); }}>
              Gestionar usuarios
            </button>
          )}
          {user.role === 'superadmin' && (
            <button className="auth-dropdown-item" onClick={() => { setShowSuper(true); setOpen(false); }}>
              Consola: clientes y planes
            </button>
          )}
          <button className="auth-dropdown-item auth-dropdown-danger" onClick={() => { void logout(); setOpen(false); }}>
            Cerrar sesión
          </button>
        </div>
      )}
      {showChangePw && (
        <ChangePasswordModal
          forced={user.mustChangePassword}
          onClose={() => setShowChangePw(false)}
        />
      )}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
      {showSuper && (
        <Suspense fallback={<div className="console-overlay" role="status"><p className="auth-hint" style={{ margin: 'auto' }}>Cargando consola…</p></div>}>
          <SuperAdminConsole onClose={() => setShowSuper(false)} />
        </Suspense>
      )}
      {showPlan && <PlanModal onClose={() => setShowPlan(false)} />}
      {legal && !legal.accepted && !user.mustChangePassword && <TermsModal />}
    </div>
    {linkModals}
    </>
  );
}

// ─── Login ───────────────────────────────────────────────────────────────

/** El mismo contacto que publica la landing (terrakairos.mx). */
const CONTACT_EMAIL = 'contacto@terrakairos.mx';

function LoginModal({ onClose, onForgot }: { onClose: () => void; onForgot: (email: string) => void }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBlocked(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? msg(err.code) : 'No pudimos conectar. Revisa tu red.');
      // Plan vencido o cuenta suspendida: no es un error de contraseña; se
      // ofrece avisar a TerraKairos desde aquí mismo.
      if (err instanceof ApiError && (err.code === 'plan_expired' || err.code === 'org_suspended')) setBlocked(err.code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Iniciar sesión" onClose={onClose}>
      <form className="auth-form" onSubmit={submit}>
        <label>
          Correo
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="username"
            required
          />
        </label>
        <label>
          Contraseña
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <button type="button" className="auth-link auth-forgot" onClick={() => onForgot(email.trim())}>
          ¿Olvidaste tu contraseña?
        </button>
        {error && <div className="auth-error">{error}</div>}
        {blocked && <RenewalRequest email={email} reason={blocked} />}
        <button type="submit" className="auth-submit" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
        <p className="auth-hint">
          ¿No tienes cuenta? Las cuentas las abre TerraKairos: escríbenos a{' '}
          <a className="auth-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </form>
    </Modal>
  );
}

// ─── Olvidé mi contraseña y enlaces por correo (RFC-0026) ────────────────

function ForgotPasswordModal({ initialEmail, onClose }: { initialEmail: string; onClose: () => void }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.authForgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? msg(err.code) : 'No pudimos conectar. Revisa tu red.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Restablecer contraseña" onClose={onClose}>
      {sent ? (
        <div className="auth-form">
          <div className="auth-ok">
            Si hay una cuenta con <strong>{email.trim()}</strong>, te llegará un correo con un enlace para elegir una contraseña nueva.
          </div>
          <p className="auth-hint">
            El enlace vence en 2 horas y sólo sirve una vez. Si no lo ves en unos minutos, revisa spam o pide ayuda al administrador de tu organización.
          </p>
          <button type="button" className="auth-submit" onClick={onClose}>Listo</button>
        </div>
      ) : (
        <form className="auth-form" onSubmit={submit}>
          <p className="auth-hint" style={{ marginTop: 0 }}>
            Escribe el correo de tu cuenta y te mandamos un enlace para elegir una contraseña nueva.
          </p>
          <label>
            Correo
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus autoComplete="username" required />
          </label>
          {error && <div className="auth-error">{error}</div>}
          <button type="submit" className="auth-submit" disabled={busy}>
            {busy ? 'Enviando…' : 'Enviar enlace'}
          </button>
        </form>
      )}
    </Modal>
  );
}

/**
 * Llega desde el enlace de una invitación o de un restablecimiento: la
 * persona elige su contraseña y entra directo, sin pasar por el login.
 */
function TokenModal({ token, onClose, onForgot }: {
  token: string;
  onClose: () => void;
  onForgot: (email: string) => void;
}) {
  const { completeToken, user } = useAuth();
  const [info, setInfo] = useState<{ purpose: 'invite' | 'reset'; email: string; name: string | null } | null>(null);
  const [blockedCode, setBlockedCode] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'blocked' | 'done'>('loading');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [blockedMsg, setBlockedMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.authTokenInspect(token)
      .then((r) => { if (alive) { setInfo(r); setState('ready'); } })
      .catch(() => { if (alive) setState('invalid'); });
    return () => { alive = false; };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    if (next.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    setBusy(true);
    try {
      const purpose = await completeToken(token, next);
      setState('done');
      // Una prueba recién activada merece una bienvenida con lo que incluye
      // (se cierra a mano); un restablecimiento sólo confirma y se va.
      if (purpose !== 'invite') window.setTimeout(onClose, 1500);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403 && err.body.passwordSet) {
        setBlockedMsg(msg(err.code));
        setBlockedCode(err.code);
        setState('blocked');
      } else if (err instanceof ApiError && err.code === 'invalid_token') {
        setState('invalid');
      } else {
        setError(err instanceof ApiError ? msg(err.code) : 'No pudimos guardar la contraseña. Revisa tu red.');
      }
    } finally {
      setBusy(false);
    }
  }

  const invite = info?.purpose === 'invite';
  const title = state === 'invalid' ? 'Enlace no válido' : invite ? 'Crea tu contraseña' : 'Elige una contraseña nueva';

  return (
    <Modal title={title} onClose={onClose}>
      {state === 'loading' && <p className="auth-hint">Verificando el enlace…</p>}
      {state === 'invalid' && (
        <div className="auth-form">
          <p className="auth-hint" style={{ marginTop: 0 }}>
            Este enlace ya se usó o venció. Los enlaces sirven una sola vez: pide uno nuevo con tu correo.
          </p>
          <button type="button" className="auth-submit" onClick={() => onForgot(info?.email ?? '')}>Pedir un enlace nuevo</button>
        </div>
      )}
      {state === 'blocked' && (
        <div className="auth-form">
          <div className="auth-ok">Tu contraseña quedó guardada.</div>
          <div className="auth-error">{blockedMsg}</div>
          {blockedCode && info && <RenewalRequest email={info.email} reason={blockedCode} />}
          <button type="button" className="auth-submit" onClick={onClose}>Entendido</button>
        </div>
      )}
      {state === 'done' && (
        invite && user?.organization?.plan === 'TRIAL' ? (
          <div className="auth-form">
            <div className="auth-ok">Listo{info?.name ? `, ${info.name}` : ''}: tu cuenta está activa ✓</div>
            <p className="auth-hint" style={{ marginTop: 0 }}>
              {user.organization.activeUntil ? <>Vigente hasta el <strong>{fmtDate(user.organization.activeUntil)}</strong>. </> : null}
              Ya ves la ubicación exacta de cada inmueble en el mapa; abre la lista o haz clic en un punto para ver su ficha
              {user.entitlements?.limits.property_views_per_month ? ` (${fmtNum(user.entitlements.limits.property_views_per_month)} fichas al mes)` : ''}.
              Puedes invitar a tu equipo desde el menú de tu cuenta → Gestionar usuarios.
            </p>
            <button type="button" className="auth-submit" onClick={() => { onClose(); openResultsList(); }} autoFocus>
              Empezar: ver las propiedades
            </button>
          </div>
        ) : (
          <div className="auth-ok">Listo{info?.name ? `, ${info.name}` : ''}: ya entraste a TerraKairos ✓</div>
        )
      )}
      {state === 'ready' && info && (
        <form className="auth-form" onSubmit={submit}>
          <p className="auth-hint" style={{ marginTop: 0 }}>
            {invite ? 'Bienvenido a TerraKairos. ' : ''}Tu usuario es <strong>{info.email}</strong>.
            {' '}Elige una contraseña de al menos 8 caracteres.
          </p>
          <input type="email" value={info.email} autoComplete="username" readOnly hidden />
          <label>
            {invite ? 'Contraseña' : 'Nueva contraseña'}
            <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" autoFocus required />
          </label>
          <label>
            Confirmar
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
          </label>
          {error && <div className="auth-error">{error}</div>}
          <button type="submit" className="auth-submit" disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar y entrar'}
          </button>
        </form>
      )}
    </Modal>
  );
}

/**
 * Términos y aviso de privacidad (RFC-0026). Sólo aparece si el dueño
 * publicó los textos y definió LEGAL_TERMS_VERSION; no se puede cerrar
 * sin aceptar (o cerrar sesión). La aceptación queda en la bitácora.
 */
function TermsModal() {
  const { legal, acceptTerms, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!legal) return null;

  async function accept() {
    if (!legal || !checked || busy) return;
    setBusy(true);
    setError(null);
    try {
      await acceptTerms(legal.version);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'version_mismatch'
        ? 'Los términos se actualizaron mientras leías. Recarga la página para ver la versión vigente.'
        : 'No pudimos registrar tu aceptación. Intenta de nuevo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Términos y aviso de privacidad" onClose={() => undefined} closable={false}>
      <div className="auth-form">
        <p className="auth-hint" style={{ marginTop: 0 }}>
          Para seguir usando TerraKairos, lee y acepta los{' '}
          <a href={legal.termsUrl} target="_blank" rel="noopener noreferrer">Términos y condiciones</a> y el{' '}
          <a href={legal.privacyUrl} target="_blank" rel="noopener noreferrer">Aviso de privacidad</a> (versión {legal.version}).
        </p>
        <label className="auth-check">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          <span>Leí y acepto los Términos y condiciones y el Aviso de privacidad.</span>
        </label>
        {error && <div className="auth-error">{error}</div>}
        <button type="button" className="auth-submit" disabled={!checked || busy} onClick={accept}>
          {busy ? 'Guardando…' : 'Aceptar y continuar'}
        </button>
        <button type="button" className="auth-text-btn" onClick={() => void logout()}>
          Cerrar sesión
        </button>
      </div>
    </Modal>
  );
}

// ─── Cambio de contraseña ────────────────────────────────────────────────

function ChangePasswordModal({ forced, onClose }: { forced: boolean; onClose: () => void }) {
  const { changePassword, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) { setError('Las contraseñas nuevas no coinciden.'); return; }
    if (next.length < 8) { setError('La nueva contraseña debe tener al menos 8 caracteres.'); return; }
    setBusy(true);
    try {
      await changePassword(current, next);
      setDone(true);
      window.setTimeout(onClose, 1200);
    } catch (err) {
      setError(err instanceof ApiError ? msg(err.code) : 'No pudimos cambiar la contraseña.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={forced ? 'Establece tu contraseña' : 'Cambiar contraseña'}
      onClose={onClose}
      closable={!forced}
    >
      {forced && (
        <p className="auth-hint" style={{ marginTop: 0 }}>
          Tu cuenta usa una contraseña temporal. Define una nueva para continuar.
        </p>
      )}
      <form className="auth-form" onSubmit={submit}>
        <label>
          Contraseña actual
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
        </label>
        <label>
          Nueva contraseña
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required />
        </label>
        <label>
          Confirmar nueva
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
        </label>
        {error && <div className="auth-error">{error}</div>}
        {done && <div className="auth-ok">Contraseña actualizada ✓</div>}
        <button type="submit" className="auth-submit" disabled={busy || done}>
          {busy ? 'Guardando…' : 'Guardar'}
        </button>
        {forced && (
          <button type="button" className="auth-text-btn" onClick={() => void logout()}>
            Cerrar sesión
          </button>
        )}
      </form>
    </Modal>
  );
}

// ─── Panel admin ─────────────────────────────────────────────────────────


/**
 * Bandeja de solicitudes de acceso (RFC-0021 §1.4). Aprobar por lotes
 * crea las cuentas y manda a cada una su invitación por correo (enlace
 * para crear su contraseña, RFC-0026). Sólo si el correo no sale se
 * muestran contraseñas temporales para entregarlas a mano.
 */
function AccessRequestsInbox({ onApproved }: { onApproved: () => void }) {
  const [items, setItems] = useState<AccessRequest[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<Array<{ email: string; pw: string; org: string | null }>>([]);

  async function load() {
    try {
      const r = await api.adminAccessRequests('PENDING');
      setItems(r.items);
      setSel(new Set());
    } catch {
      setItems([]);
    }
  }
  useEffect(() => { void load(); }, []);

  function toggle(id: string) {
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function approve() {
    if (sel.size === 0 || busy) return;
    setBusy(true); setNote(null);
    try {
      const r = await api.adminApproveAccess([...sel]);
      // Se reporta cuántas cuentas quedaron SIN correo enviado: hay que
      // entregar esas credenciales por otra vía, y fingir éxito sería peor.
      const sinCorreo = r.items.filter((i) => !i.emailed);
      setNote(
        `${r.approved} aprobada(s).` +
        (sinCorreo.length > 0 ? ` ${sinCorreo.length} sin correo enviado — entrega esas contraseñas temporales a mano.` : ' Invitaciones enviadas por correo.') +
        (r.failed.length > 0 ? ` ${r.failed.length} fallaron (${r.failed.map((f) => f.reason).join(', ')}).` : '')
      );
      setDelivered(sinCorreo.filter((i) => i.tempPassword).map((i) => ({ email: i.email, pw: i.tempPassword!, org: i.organizationName })));
      await load();
      onApproved();
    } catch {
      setNote('No pudimos procesar la aprobación.');
    } finally { setBusy(false); }
  }

  async function reject() {
    if (sel.size === 0 || busy) return;
    setBusy(true); setNote(null);
    try {
      const r = await api.adminRejectAccess([...sel]);
      setNote(`${r.rejected} rechazada(s).`);
      await load();
    } catch {
      setNote('No pudimos procesar el rechazo.');
    } finally { setBusy(false); }
  }

  if (items.length === 0) return null;

  return (
    <section className="admin-inbox">
      <div className="section-title">Solicitudes de acceso ({items.length})</div>
      <div className="admin-inbox-list">
        {items.map((r) => (
          <label key={r.id} className="admin-inbox-row">
            <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} />
            <div className="admin-inbox-main">
              <div className="admin-inbox-name">
                {r.name} · <span className="admin-inbox-inst">{r.institution}</span>
              </div>
              <div className="admin-inbox-meta">{r.role_title} · {r.email}</div>
              {r.reason && <div className="admin-inbox-reason">{r.reason}</div>}
            </div>
          </label>
        ))}
      </div>
      <div className="admin-inbox-actions">
        <button type="button" className="auth-submit" disabled={busy || sel.size === 0} onClick={approve}>
          Aprobar {sel.size > 0 ? `(${sel.size})` : ''}
        </button>
        <button type="button" className="results-action" disabled={busy || sel.size === 0} onClick={reject}>
          Rechazar
        </button>
      </div>
      {note && <div className="auth-hint">{note}</div>}
      {delivered.map((d) => (
        <div key={d.email} className="admin-temp-pw">
          {d.org && <>Organización <strong>{d.org}</strong> · </>}
          Contraseña temporal de <strong>{d.email}</strong>: <code>{d.pw}</code>
          <span className="auth-hint">Cópiala y compártela; no se vuelve a mostrar.</span>
        </div>
      ))}
    </section>
  );
}

// ─── Plan de la organización (RFC-0025) ─────────────────────────────────

/**
 * Indicador compacto en el header: sólo aparece cuando hay algo que decir
 * — prueba en curso, vencimiento cercano o cupo de fichas al 80%.
 */
function PlanPill({ onOpen }: { onOpen: () => void }) {
  const { user, usage } = useAuth();
  if (!user?.organization || !user.entitlements) return null;
  const days = daysUntil(user.organization.activeUntil);
  const viewsLimit = user.entitlements.limits.property_views_per_month;
  const views = usage?.property_views ?? 0;
  const nearQuota = viewsLimit !== null && viewsLimit > 0 && views / viewsLimit >= 0.8;
  const isTrial = user.organization.plan === 'TRIAL';
  const expiring = days !== null && days <= 14;
  if (!isTrial && !expiring && !nearQuota) return null;
  const parts: string[] = [isTrial ? 'Prueba' : (user.entitlements.planName ?? user.organization.plan)];
  if (days !== null) parts.push(days <= 0 ? 'vencido' : days === 1 ? 'vence mañana' : `${days} días`);
  if (viewsLimit !== null) parts.push(`${fmtNum(views)}/${fmtNum(viewsLimit)} fichas`);
  const tone = (days !== null && days <= 3) || (viewsLimit !== null && views >= viewsLimit) ? 'bad' : expiring || nearQuota ? 'warn' : 'info';
  return (
    <button type="button" className={`plan-pill ${tone}`} onClick={onOpen} title="Ver tu plan y consumo">
      {parts.join(' · ')}
    </button>
  );
}

/** "Mi plan y consumo": disponible para cualquier cuenta de una organización. */
function PlanModal({ onClose }: { onClose: () => void }) {
  const { user, usage } = useAuth();
  if (!user?.organization || !user.entitlements) return null;
  const org = user.organization;
  const days = daysUntil(org.activeUntil);
  return (
    <Modal title="Mi plan y consumo" onClose={onClose} wide>
      <p className="auth-hint" style={{ marginTop: 0 }}>
        <strong>{org.name}</strong> · plan {user.entitlements.planName ?? org.plan}
        {org.activeUntil && <> · {days !== null && days <= 0 ? 'venció' : 'vence'} el {fmtDate(org.activeUntil)}</>}
      </p>
      <PlanSummary entitlements={user.entitlements} usage={usage} />
      <p className="auth-hint">Los cupos mensuales se reinician el día 1.</p>
      <UpgradePlans currentPlan={org.plan} email={user.email} />
    </Modal>
  );
}

// ─── Usuarios de la organización ────────────────────────────────────────

function AdminPanel({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [seats, setSeats] = useState<OrganizationSeats | null>(null);
  const [myOrg, setMyOrg] = useState<MyOrganization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Alta
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'admin' | 'analyst'>('analyst');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<DeliveryResult | null>(null);

  const isSuper = user?.role === 'superadmin';

  async function load() {
    setLoading(true);
    try {
      const r = await api.adminListUsers();
      setUsers(r.users);
      setSeats(r.organization);
      setError(null);
      if (!isSuper) {
        try { setMyOrg(await api.adminOrganization()); } catch { setMyOrg(null); }
      }
    } catch (err) {
      setError(err instanceof ApiError ? msg(err.code) : 'No pudimos cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setCreateErr(null);
    setDelivered(null);
    setCreating(true);
    try {
      const r = await api.adminCreateUser(email.trim(), name.trim() || null, role);
      setDelivered({ email: r.user.email, delivery: r.delivery, tempPassword: r.tempPassword, kind: 'invite' });
      setEmail(''); setName(''); setRole('analyst');
      await load();
    } catch (err) {
      setCreateErr(errMsg(err, 'No pudimos crear la cuenta.'));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Modal title={seats ? `Usuarios · ${seats.name}` : 'Usuarios'} onClose={onClose} wide>
      {seats && (
        <div className="admin-seats" title="Cuentas activas sobre el cupo del plan. Las inactivas no ocupan asiento.">
          <strong>{seats.seatsUsed}</strong> de <strong>{seats.seatLimit}</strong> asientos · plan {myOrg?.organization.planName ?? seats.plan}
          {seats.activeUntil && <> · vence {fmtDate(seats.activeUntil)}</>}
          {seats.suspended && <span className="flag flag-warn" style={{ marginLeft: 8 }}>Suspendida</span>}
        </div>
      )}
      {isSuper && (
        <p className="auth-hint" style={{ marginTop: 0 }}>
          Vista de todas las cuentas. Para dar de alta usuarios en un cliente, usa la consola de clientes.
        </p>
      )}
      {/* Prospectos de toda la plataforma: sólo el dueño (superadmin). */}
      {isSuper && <AccessRequestsInbox onApproved={() => { void load(); }} />}
      {!isSuper && (
        <form className="auth-form admin-create" onSubmit={create}>
          <div className="admin-create-row">
            <input type="email" placeholder="correo@dominio.mx" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <input type="text" placeholder="Nombre (opcional)" value={name} onChange={(e) => setName(e.target.value)} />
            <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'analyst')}>
              <option value="analyst">Analista</option>
              <option value="admin">Admin</option>
            </select>
            <button type="submit" className="auth-submit" disabled={creating}>
              {creating ? '…' : 'Crear'}
            </button>
          </div>
          {createErr && <div className="auth-error">{createErr}</div>}
        </form>
      )}
      {delivered && <DeliveryNotice r={delivered} />}
      {actionErr && <div className="auth-error">{actionErr}</div>}

      <div className="admin-users">
        {loading ? (
          <p className="auth-hint">Cargando…</p>
        ) : error ? (
          <div className="auth-error">{error}</div>
        ) : (
          <div className="table-scroll">
            <table className="admin-table">
              <thead>
                <tr><th>Cuenta</th><th>Estado</th><th>Último acceso</th><th>Acciones</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={u.active ? '' : 'admin-inactive'}>
                    <td>
                      <div>{u.name ?? u.email}</div>
                      {u.name && <div className="admin-subemail">{u.email}</div>}
                      {u.organization_name && isSuper && <div className="admin-subemail">{u.organization_name}</div>}
                      {u.role === 'superadmin' && <div className="admin-subemail">{ROLE_LABELS.superadmin}</div>}
                    </td>
                    <td>{u.active ? 'Activo' : 'Inactivo'}{u.must_change_password ? ' · contraseña temporal' : ''}</td>
                    <td>{fmtDate(u.last_login_at)}</td>
                    <td>
                      <UserRowActions
                        user={u}
                        isSelf={u.id === user?.id}
                        onChanged={() => { setActionErr(null); void load(); }}
                        onReset={setDelivered}
                        onError={setActionErr}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {myOrg && (
        <section className="admin-section">
          <div className="section-title">Plan y consumo de la organización ({myOrg.usage.period})</div>
          <PlanSummary entitlements={myOrg.entitlements} usage={myOrg.usage} />
          <p className="auth-hint">Mes anterior: {fmtNum(myOrg.usagePrevious.property_views)} fichas abiertas.</p>
        </section>
      )}
      {/* Siempre visible: aun sin API en el plan hay que poder revocar llaves viejas. */}
      {!isSuper && <ApiKeysSection />}
    </Modal>
  );
}
